import React from 'react';
import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import SectionLayout from '@layouts/section-layout';
import { InstantSearch, Configure } from 'react-instantsearch';
import { Results } from './results';
import {
  AutomatedListing,
  AutomatedListingTileBlockProps,
} from './automated-listing-tile-block.types';

// Verify environment variables exist
const GATSBY_ALGOLIA_APP_ID: string | undefined =
  process.env.GATSBY_ALGOLIA_APP_ID;
const GATSBY_ALGOLIA_API_KEY: string | undefined =
  process.env.GATSBY_ALGOLIA_API_KEY;
const GATSBY_ALGOLIA_INDEX_NAME: string | undefined =
  process.env.GATSBY_ALGOLIA_INDEX_NAME;

if (!GATSBY_ALGOLIA_APP_ID && process.env.NODE_ENV !== 'test') {
  throw new Error(`Missing env var "GATSBY_ALGOLIA_APP_ID"`);
}
if (!GATSBY_ALGOLIA_API_KEY && process.env.NODE_ENV !== 'test') {
  throw new Error(`Missing env var "GATSBY_ALGOLIA_API_KEY"`);
}
if (!GATSBY_ALGOLIA_INDEX_NAME && process.env.NODE_ENV !== 'test') {
  throw new Error(`Missing env var "GATSBY_ALGOLIA_INDEX_NAME"`);
}

// Initialise the client
const searchClient: SearchClient = algoliasearch(
  GATSBY_ALGOLIA_APP_ID as string,
  GATSBY_ALGOLIA_API_KEY as string
);
const algoliaPrimaryIndexName: string = GATSBY_ALGOLIA_INDEX_NAME as string;

// Returns the name of the Algolia index to search based on the properties in the given
// automated listing block object.
function getAlgoliaIndex(automatedListing: AutomatedListing): string {
  switch (true) {
    case Boolean(automatedListing.sortByMostRecent): // Be 100% sure this is a boolean as we have no types here.
      return `${algoliaPrimaryIndexName}_date-desc`;

    default:
      return algoliaPrimaryIndexName;
  }
}

// This component is used to render the automated listing tile block.
const AutomatedListingTileBlock: React.FC<AutomatedListingTileBlockProps> = ({
  localeCode,
  pageColour,
  automatedListing,
  currentEntryId,
}) => {
  const filters = ((topics, geography, contentType) => {
    const hasAllTopics = topics && topics.includes('All');
    const hasAllContentTypes = contentType && contentType.includes('All');

    // Map topics if "All" is not present
    const mappedTopics =
      topics && !hasAllTopics
        ? topics.map(topic => {
            let filteredTopic = `topics.title:"${topic}"`;
            if (topic === 'None applicable') {
              filteredTopic = `noneApplicable:true`;
            }
            return filteredTopic;
          })
        : [];

    // Map contentType if "All" is not present
    const mappedContentType =
      contentType && !hasAllContentTypes
        ? contentType.map(
            contentType => `resourceContentType.title:"${contentType}"`
          )
        : [];

    const mappedGeography = geography
      ? geography.map(geography => `geography.title:"${geography}"`)
      : [];

    const parametersFilters = [
      mappedTopics.join(' OR '),
      mappedContentType.join(' OR '),
      mappedGeography.join(' OR '),
    ];

    const cleanFilters = parametersFilters.filter(
      parameter => parameter !== ''
    );

    const filtersQuery =
      cleanFilters.length > 0 ? ` AND (${cleanFilters.join(') AND (')})` : '';

    const queryFilters = `locale:${localeCode} AND NOT objectID:${currentEntryId}${filtersQuery}`;

    return queryFilters;
  })(
    automatedListing.topicsTags,
    automatedListing.filterByGeography,
    automatedListing.filterByContentType
  );

  const indexName = getAlgoliaIndex(automatedListing);

  // TODO: Upgrade to latest InstantSearch v7, with analytics if required - See filter-result & resultsblock for example
  return (
    <SectionLayout className="" isCSI={false}>
      <InstantSearch indexName={indexName} searchClient={searchClient}>
        <Configure
          // @ts-ignore - filters prop is valid but type definitions are incorrect
          filters={filters}
          hitsPerPage={40}
        />
        <Results
          locale={localeCode}
          tileBlockProps={{
            title: automatedListing.title,
            anchorId: automatedListing.anchorId,
            description: automatedListing.automatedListingDescription,
            cta: {
              text: automatedListing.callToActionText,
              url: automatedListing.callToActionLink,
            },
            numberOfTiles: automatedListing.numberOfTiles,
            visibleTilesCount: automatedListing.visibleTilesCount,
            manualTilesOverwritten: automatedListing.manualTilesOverwritten,
            isCarousel: automatedListing.displayAsCarousel,
            pageColour,
          }}
        />
      </InstantSearch>
    </SectionLayout>
  );
};
export default AutomatedListingTileBlock;
